import React, {useEffect, useState} from "react"
import {Alert, Chip, Divider, LinearProgress, Stack, Typography, useTheme} from "@mui/joy"
import {Link} from "react-router-dom"
import {AtSign, Calendar, KeyRound, LogOut, Pencil, ShieldCheck, Sparkles, UserIcon, Zap} from "lucide-react"
import Button from "../components/Button"
import Card from "../components/Card"
import Input from "../components/Input"
import GoogleIcon from "../components/GoogleIcon"
import ReferralCard from "../components/ReferralCard"
import {useAuth} from "../hooks/useAuth"
import {authErrorMessage} from "../context/AuthContext"
import {useFeedback} from "../hooks/useFeedback"
import {Subscription} from "../types/subscription"
import {activePalette, applyTransparency} from "../theme"
import {TIER_LABELS, isSubscriptionActive, remainingAnalyses, usedAnalysesToday} from "../values/plans"
import {useTranslation} from "react-i18next"
import PageContainer from "../components/layout/PageContainer"

const MIN_PASSWORD_LENGTH = 8

const STATUS_COLORS: { [status in Subscription["status"]]: "success" | "warning" | "danger" | "neutral" } = {
    active: "success",
    paused: "warning",
    expired: "danger",
    cancelled: "neutral"
}

/** A status is a word on screen, so it is read from the translations rather than printed raw */
const STATUS_KEYS: { [status in Subscription["status"]]: string } = {
    active: "statusActive",
    paused: "statusPaused",
    expired: "statusExpired",
    cancelled: "statusCancelled"
}

const formatDate = (value?: number) => value ? new Date(value).toLocaleDateString(undefined, {
    year: "numeric",
    month: "long",
    day: "numeric"
}) : "—"

/** Two letters is all the room the avatar has */
const initialsOf = (name?: string) => (name || "")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map(part => part[0]?.toUpperCase())
    .join("") || "?"

const Account = () => {
    const theme = useTheme()
    const palette = activePalette(theme)
    const auth = useAuth()
    const feedback = useFeedback()
    const {t} = useTranslation()

    const user = auth.user
    const subscription = user?.subscription

    const [name, setName] = useState(user?.name || "")
    const [editingName, setEditingName] = useState(false)
    const [currentPassword, setCurrentPassword] = useState("")
    const [newPassword, setNewPassword] = useState("")
    const [confirmPassword, setConfirmPassword] = useState("")
    const [passwordError, setPasswordError] = useState("")

    // The account arrives after the first render, once the stored session has been resolved
    useEffect(() => setName(user?.name || ""), [user?.name])

    // An access that has run out grants nothing, whatever counters are still written on it
    const active = isSubscriptionActive(subscription)
    // Discounted for the day, like the API does: a stale tally would misreport the quota
    const usedToday = active ? usedAnalysesToday(subscription) : 0
    const dailyLimit = active ? subscription?.dailyPredictionsLimit || 0 : 0
    const usageRatio = dailyLimit ? Math.min(100, (usedToday / dailyLimit) * 100) : 0
    const usageColor = usageRatio >= 100 ? "danger" : usageRatio >= 70 ? "warning" : "success"

    const saveName = async () => {
        if (!name.trim()) {
            feedback.showFeedback(t("nameEmpty"), "danger")
            return
        }

        try {
            await auth.changeName?.(name.trim())
            setEditingName(false)
            feedback.showFeedback(t("nameUpdated"), "success")
        } catch (e) {
            feedback.showFeedback(authErrorMessage(e), "danger")
        }
    }

    const savePassword = async (event: React.FormEvent) => {
        event.preventDefault()

        if (newPassword.length < MIN_PASSWORD_LENGTH) {
            setPasswordError(t("passwordTooShort", {min: MIN_PASSWORD_LENGTH}))
            return
        }

        if (newPassword !== confirmPassword) {
            setPasswordError(t("passwordsMustMatch"))
            return
        }

        setPasswordError("")

        // Read before the call: setting a password turns the account into a password account
        const settingPassword = !auth.isPasswordAccount

        try {
            if (settingPassword)
                await auth.setPassword?.(newPassword)
            else
                await auth.changePassword?.(currentPassword, newPassword)

            setCurrentPassword("")
            setNewPassword("")
            setConfirmPassword("")
            feedback.showFeedback(t(settingPassword ? "passwordSet" : "passwordChanged"), "success")
        } catch (e) {
            setPasswordError(authErrorMessage(e))
        }
    }

    return <PageContainer gap={3} width={{md: "38rem", lg: "52rem"}}>
        {/* Identity */}
        <Card>
            <Stack direction={{xs: "column", sm: "row"}} alignItems={"center"} gap={2.5}>
                <Stack alignItems={"center"} justifyContent={"center"}
                       sx={{
                           width: 72,
                           height: 72,
                           borderRadius: "50%",
                           flexShrink: 0,
                           background: `linear-gradient(45deg, ${palette.primary["500"]}, #2DDF82)`
                       }}>
                    <Typography level={"h3"} textColor={"white"}>{initialsOf(user?.name)}</Typography>
                </Stack>

                <Stack gap={0.5} flex={1} minWidth={0} alignItems={{xs: "center", sm: "flex-start"}}>
                    <Typography level={"h3"} textAlign={{xs: "center", sm: "left"}} sx={{overflowWrap: "anywhere"}}>
                        {user?.name || "—"}
                    </Typography>
                    <Typography level={"body-sm"} startDecorator={<AtSign size={14}/>} sx={{overflowWrap: "anywhere"}}>
                        {user?.email || "—"}
                    </Typography>
                    <Stack direction={"row"} gap={1} paddingTop={0.5} flexWrap={"wrap"}>
                        <Chip size={"sm"} variant={"soft"} color={"primary"} startDecorator={<Sparkles size={14}/>}>
                            {t("planSuffix", {plan: TIER_LABELS[subscription?.tier || "free"]})}
                        </Chip>
                        <Chip size={"sm"} variant={"soft"}
                              startDecorator={auth.isPasswordAccount ? <KeyRound size={14}/> : <GoogleIcon size={14}/>}>
                            {auth.isPasswordAccount ? t("emailAndPassword") : t("googleLabel")}
                        </Chip>
                    </Stack>
                </Stack>
            </Stack>
        </Card>

        {/* Profile */}
        <Card>
            <Typography level={"title-lg"} startDecorator={<UserIcon/>}>{t("profile")}</Typography>

            <Stack gap={1}>
                <Typography level={"body-xs"} textTransform={"uppercase"} letterSpacing={"0.1em"}>
                    {t("displayName")}
                </Typography>

                {editingName
                    ? <Stack direction={{xs: "column", sm: "row"}} gap={1} alignItems={"stretch"}>
                        <Stack flex={1}>
                            <Input value={name} placeholder={t("yourNamePlaceholder")} autoFocus
                                   onChange={e => setName(e.target.value)}/>
                        </Stack>
                        <Stack direction={"row"} gap={1}>
                            <Button onClick={saveName} loading={auth.submitting}>{t("save")}</Button>
                            <Button variant={"plain"} color={"neutral"}
                                    onClick={() => {
                                        setName(user?.name || "")
                                        setEditingName(false)
                                    }}
                                    sx={{background: "transparent", color: "text.tertiary"}}>
                                {t("cancel")}
                            </Button>
                        </Stack>
                    </Stack>
                    : <Stack direction={"row"} alignItems={"center"} justifyContent={"space-between"} gap={2}>
                        <Typography level={"title-md"}>{user?.name || "—"}</Typography>
                        <Button size={"sm"} variant={"soft"} startDecorator={<Pencil size={16}/>}
                                onClick={() => setEditingName(true)}>
                            {t("edit")}
                        </Button>
                    </Stack>}
            </Stack>

            <Divider/>

            <Stack gap={1}>
                <Typography level={"body-xs"} textTransform={"uppercase"} letterSpacing={"0.1em"}>
                    {t("emailLabel")}
                </Typography>
                <Typography level={"title-md"} sx={{overflowWrap: "anywhere"}}>{user?.email || "—"}</Typography>
                <Typography level={"body-xs"}>
                    {t("emailNotice")}
                </Typography>
            </Stack>

            {!!user?.createdAt && <>
                <Divider/>
                <Typography level={"body-sm"} startDecorator={<Calendar size={16}/>}>
                    {t("memberSince", {date: formatDate(user.createdAt)})}
                </Typography>
            </>}
        </Card>

        {/* Plan */}
        <Card>
            <Stack direction={"row"} alignItems={"center"} justifyContent={"space-between"} gap={2}>
                <Typography level={"title-lg"} startDecorator={<Zap/>}>{t("yourPlan")}</Typography>
                {subscription && <Chip size={"sm"} variant={"soft"} color={STATUS_COLORS[subscription.status]}>
                    {t(STATUS_KEYS[subscription.status] || subscription.status)}
                </Chip>}
            </Stack>

            <Stack direction={"row"} alignItems={"baseline"} gap={1}>
                <Typography level={"h2"}>{TIER_LABELS[subscription?.tier || "free"]}</Typography>
                {!!subscription?.price && <Typography level={"body-sm"}>
                    {subscription.price.toLocaleString("fr-FR")} {subscription.currency}
                </Typography>}
            </Stack>

            <Stack gap={1}
                   sx={{
                       padding: 2,
                       borderRadius: "13px",
                       background: applyTransparency(palette.primary["500"], 0.12),
                       border: `1px solid ${applyTransparency(palette.primary["600"], 0.3)}`
                   }}>
                <Stack direction={"row"} justifyContent={"space-between"} alignItems={"center"} gap={2}>
                    <Typography level={"body-sm"}>{t("analysesUsedToday")}</Typography>
                    <Typography level={"title-sm"}>{usedToday} / {dailyLimit || "—"}</Typography>
                </Stack>
                <LinearProgress determinate value={usageRatio} color={usageColor}/>
                <Typography level={"body-xs"}>
                    {usageRatio >= 100
                        ? t("dailyLimitReached")
                        : t("leftUntilTomorrow", {count: remainingAnalyses(subscription)})}
                </Typography>
            </Stack>

            {subscription && subscription.tier !== "free" && <Typography level={"body-sm"}
                                                                        startDecorator={<Calendar size={16}/>}>
                {subscription.status === "active" ? t("renewsOn") : t("endedOn")} {formatDate(subscription.expiresAt)}
            </Typography>}

            <Link to={"/pricing"} style={{textDecoration: "none"}}>
                <Button fullWidth startDecorator={<Sparkles size={18}/>}>
                    {subscription?.tier === "platinum" ? t("seeAllPlans") : t("upgradeMyPlan")}
                </Button>
            </Link>
        </Card>

        {/* Referrals */}
        <ReferralCard/>

        {/* Security */}
        <Card>
            <Typography level={"title-lg"} startDecorator={<ShieldCheck/>}>{t("security")}</Typography>

            <Stack component={"form"} onSubmit={savePassword} gap={2}>
                {auth.isPasswordAccount
                    ? <Typography level={"body-sm"}>{t("changePasswordNotice")}</Typography>
                    : <Typography level={"body-sm"} startDecorator={<GoogleIcon size={16}/>}>
                        {t("setPasswordNotice")}
                    </Typography>}

                {passwordError && <Alert color={"danger"} variant={"soft"}>{passwordError}</Alert>}

                {/* A Google account has no current password: the Google sign in is what vouches for it */}
                {auth.isPasswordAccount &&
                    <Input type={"password"} placeholder={t("currentPassword")} autoComplete={"current-password"}
                           value={currentPassword} onChange={e => setCurrentPassword(e.target.value)}/>}
                <Input type={"password"} placeholder={t("newPassword")} autoComplete={"new-password"}
                       value={newPassword} onChange={e => setNewPassword(e.target.value)}/>
                <Input type={"password"} placeholder={t("confirmNewPassword")} autoComplete={"new-password"}
                       value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)}/>

                <Button type={"submit"} loading={auth.submitting}>
                    {auth.isPasswordAccount ? t("changePassword") : t("setPassword")}
                </Button>
            </Stack>

            <Divider/>

            <Button variant={"soft"} color={"danger"} startDecorator={<LogOut size={18}/>}
                    onClick={() => auth.signOut?.()}
                    sx={{background: "rgba(255,255,255,0.06)", color: "danger.300"}}>
                {t("signOut")}
            </Button>
        </Card>
    </PageContainer>
}

export default Account
